'use client';
import { Box } from '@startuptools/ui/Box';
import Quill from 'quill';
import { Delta, EmitterSource, QuillOptions, Range } from 'quill/core';
import { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';

const ReactQuill = forwardRef<
  Quill,
  {
    readOnly?: boolean;
    defaultValue?: Delta;
    onTextChange?: (delta: Delta, oldContent: Delta, source: EmitterSource) => void;
    onSelectionChange?: (range: Range, oldRange: Range, source: EmitterSource) => void;
    value?: Delta;
  } & QuillOptions
>(({ readOnly, defaultValue, onTextChange, onSelectionChange, value, ...options }, ref) => {
  const defaultValueRef = useRef(defaultValue || value);
  const onTextChangeRef = useRef(onTextChange);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const optionsRef = useRef({ ...options, readOnly });

  const containerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    onTextChangeRef.current = onTextChange;
    onSelectionChangeRef.current = onSelectionChange;
  });

  useEffect(() => {
    if (ref && 'current' in ref) {
      ref.current?.enable(!readOnly);
    }
  }, [ref, readOnly]);

  useEffect(() => {
    if (value) {
      if (ref && 'current' in ref) {
        ref.current?.setContents(value);
      }
    }
  }, [ref, value]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) throw new Error('Container is not defined');
    const editorContainer = container.appendChild(container.ownerDocument.createElement('div'));
    const quill = new Quill(editorContainer, {
      theme: 'snow',
      ...optionsRef.current,
    });

    if (ref && 'current' in ref) {
      ref.current = quill;
    }

    if (defaultValueRef.current) {
      quill.setContents(defaultValueRef.current);
    }

    quill.on(Quill.events.TEXT_CHANGE, (...args) => {
      onTextChangeRef.current?.(...args);
    });

    quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
      onSelectionChangeRef.current?.(...args);
    });

    return () => {
      if (ref && 'current' in ref) {
        ref.current = null;
      }
      container.innerHTML = '';
    };
  }, [ref, optionsRef]);

  return (
    <>
      <Box id="quill-container" ref={containerRef}></Box>
    </>
  );
});

ReactQuill.displayName = 'ReactQuill';

export default ReactQuill;
